// LocationCapture — Phase R3+ reusable geo-input component.
//
// ════════════════════════════════════════════════════════════════════
// PURPOSE
// ════════════════════════════════════════════════════════════════════
//
// Single component used by every form that needs to capture a user's
// physical location for the Hyper-Local Mesh Network attribution
// system:
//
//   * player-frontend Register.tsx     — required when no referral code
//   * player-frontend Profile.tsx      — "Update Location" flow
//   * agent-frontend  GeoForcedModal   — one-time GPS gate after login
//   * agent-frontend  AccountPanel     — agent's own profile location
//   * agent-frontend  Sub-agent forms  — agent creates/edits a sub-agent
//   * admin-frontend  CreatePlayerModal, AdminUserDetail,
//     AdminAgentDetail, AgentsListPanel — admin onboards/edits a user
//
// Owns its OWN UI state (which step we're in, dropdown caches, etc.)
// but its OUTPUT is a single normalized geo bag passed up via
// `onChange`. The parent form just owns that bag and submits it.
//
// ════════════════════════════════════════════════════════════════════
// TWO MODES — pick via the `mode` prop
// ════════════════════════════════════════════════════════════════════
//
// `mode='self'` (DEFAULT — user is setting their OWN location):
//
//   Sequential, validated flow with the pincode as the authoritative
//   anchor and GPS as the truth check:
//
//     STEP 1 — PIN CODE FIRST:
//       Render the pincode input prominently. Resolve via
//       /api/geo/pincode-lookup. Single area → auto-fill; multiple →
//       show the picker dropdown. This step yields an authoritative
//       `pin_area_id` + the area's stored lat/lng.
//
//     STEP 2 — THEN GPS LOCK:
//       Once the pincode is resolved, surface the
//       "Use my current location" button. Required step in self-mode.
//
//     STEP 3 — 15-KM HAVERSINE VALIDATION:
//       Compute the Haversine distance between the resolved pincode
//       area's lat/lng and the device GPS lat/lng (earth radius
//       6371 km). Both branches save BOTH device lat/lng and the
//       pincode-resolved area:
//         * d ≤ 15 km  → PIN-CODE WINS for display. The user-facing
//           `area_id` is the pincode-resolved area; `gps_area_id` is
//           filled by a second /api/geo/resolve call for the record.
//         * d  > 15 km → GPS WINS for display. We call
//           /api/geo/resolve on the device coords to get the GPS-
//           side area_id and promote it to `area_id`. The pincode's
//           area_id is preserved as `pin_area_id`.
//
//     STEP 4 (FALLBACK): MANUAL CASCADE:
//       Only an escape hatch — exposed via a small toggle if the
//       pincode lookup fails entirely. Picks State → District →
//       Area without GPS verification.
//
// `mode='child'` (parent setting somebody else's location — e.g.,
// admin creates an agent, agent creates a sub-agent, admin/agent
// edits a downstream user's record):
//
//   Pincode-only. The GPS button is hidden entirely — capturing the
//   creator's GPS would attribute the new user to the creator's
//   office (a wrong-attribution land mine the locator can't unwind
//   later). No Haversine check. Manual cascade is the fallback. The
//   downstream user themselves overlays precise GPS via the
//   GeoForcedModal on their own first login.
//
//   The legacy `hideGps` prop is kept for back-compat and is exactly
//   equivalent to `mode='child'`. If both are provided, `mode` wins.
//
// ════════════════════════════════════════════════════════════════════
// SHOW-SAVED-SUMMARY (read-only card with an "Edit" button)
// ════════════════════════════════════════════════════════════════════
//
// When `value` already carries resolved location data (either a full
// state/district/area triple OR a lat/lng pair), the component opens
// in a compact read-only summary:
//
//   "Mumbai, Maharashtra · 400001"  [GPS pin saved]   [Edit location]
//
// Clicking the "Edit" button switches the component into the editor
// flow described above. Onboarding flows where `value` is empty
// (e.g., signup) skip the summary and go straight to the editor.
//
// Consumers that always want the editor (e.g., signup with no
// pre-existing value) can pass `showSavedSummary={false}` to force
// it explicitly. Default is `true`.
//
// ════════════════════════════════════════════════════════════════════
// MULTI-AREA EDGE CASE (pincode → 2+ areas)
// ════════════════════════════════════════════════════════════════════
//
// In India a single 6-digit pincode can map to multiple geographic
// areas (e.g., 401107 covers 3 areas in Thane district). When this
// happens the component:
//
//   1. Auto-fills state_id + district_id (these are unambiguous —
//      all matching pincode rows share the same district).
//   2. Renders a dropdown with the N area options, formatted as
//      "AREA NAME — DISTRICT" (district included for context).
//   3. Holds back the geo-bag emit until the user picks an area.
//   4. On pick: emits the full geo bag with the chosen area_id +
//      pincode_id.
//
// If the user changes the pincode (typed a different number), the
// dropdown clears and the cycle restarts.
//
// ════════════════════════════════════════════════════════════════════
// GEO BAG OUTPUT
// ════════════════════════════════════════════════════════════════════
//
//   {
//     latitude?, longitude?,              // device GPS (self-mode only)
//     state_id, district_id, area_id,     // prioritized area = winner
//     pincode_id?, nation,
//     pin_area_id?, gps_area_id?,         // both sides, for diagnostics
//   }
//
// `area_id` is always the WINNER (pin within 15 km / GPS if far). The
// loser is preserved on the auxiliary `*_area_id` fields. `isGeoBagComplete`
// still uses `area_id` so existing call sites are unaffected.
//
// ════════════════════════════════════════════════════════════════════
// USAGE
// ════════════════════════════════════════════════════════════════════
//
//   // Self-mode (default): pincode → GPS → 15-km validation.
//   <LocationCapture
//     value={form.geo}
//     onChange={(geo) => setForm({ ...form, geo })}
//     required={!form.referralCode}
//   />
//
//   // Child-mode (creator filling somebody else's location):
//   <LocationCapture mode="child" value={geo} onChange={setGeo} />
//
//   // Force the editor (signup, no pre-existing value):
//   <LocationCapture
//     value={EMPTY_GEO_BAG}
//     onChange={setGeo}
//     showSavedSummary={false}
//   />

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import { useT } from '../lib/i18n';

// ─── Types ─────────────────────────────────────────────────────────

export type GeoBag = {
  latitude?: number | null;
  longitude?: number | null;
  state_id?: number | null;
  district_id?: number | null;
  area_id?: number | null;
  pincode_id?: number | null;
  nation?: string;
  // Self-mode diagnostics: both sides of the 15-km Haversine test
  // are preserved so we never silently throw away either resolution.
  // The prioritized area_id is duplicated on whichever of these
  // matches the winner; the loser is preserved on the other.
  pin_area_id?: number | null;
  gps_area_id?: number | null;
  // OPTIONAL human-readable pin code string (e.g. "400001"). Some
  // backend hydration paths return both `pincode_id` AND the string
  // directly (e.g. user/agent fetch responses). When set, the
  // summary card displays this immediately without an extra
  // /api/geo/pincode/:id round-trip on first paint. Treated as a
  // pure display cache — the authoritative FK is `pincode_id`.
  pincode?: string | null;
};

// Empty bag factory — components seed their form state with this so
// the controlled-input invariant holds from first render.
export const EMPTY_GEO_BAG: GeoBag = {
  latitude: null, longitude: null,
  state_id: null, district_id: null,
  area_id: null,  pincode_id: null,
  nation: 'India',
  pin_area_id: null, gps_area_id: null,
  pincode: null,
};

// Predicate used by parent forms to decide whether the geo bag carries
// enough data for the server's locator to attach an agent. The minimum
// useful payload is one of:
//   * device GPS (latitude+longitude) — server hits L1 radius search.
//   * area_id — server hits L2 exact match (and uses area's stored
//     lat/lng for L1 fallback if available).
//   * district_id or state_id — server cascades to L3/L4/L5.
// state_id alone counts as "complete" for the purpose of unlocking
// signup, because the server will always find SOMETHING (worst case
// it falls to L5 nation match). We surface this so Register.tsx can
// keep the submit button disabled until the user picks at least one.
export function isGeoBagComplete(g: GeoBag | null | undefined): boolean {
  if (!g) return false;
  if (g.latitude != null && g.longitude != null) return true;
  if (g.area_id     != null) return true;
  if (g.district_id != null) return true;
  if (g.state_id    != null) return true;
  return false;
}

/**
 * Stricter "savable" predicate for self-mode (user/agent updating
 * THEIR OWN location). Requires BOTH device lat/lng to be set.
 *
 * Why: when the user themselves saves their own location, the
 * locked rule "agent must have precise GPS" applies. Saving with
 * only pincode/area data would immediately trigger the GeoForced
 * Modal on the next pageview, asking them to re-do the GPS step.
 * Avoid that double-step by disabling Save until GPS is locked.
 *
 * For child-mode flows (admin / parent agent editing a downline)
 * use the looser `isGeoBagComplete` instead — the parent can't
 * capture the child's GPS, so structured area data alone is OK;
 * the child gets re-prompted by the GeoForcedModal on next login.
 */
export function isGeoBagSavableForSelf(g: GeoBag | null | undefined): boolean {
  if (!g) return false;
  return g.latitude != null && g.longitude != null;
}

/**
 * Unified "located well enough to route AND not re-prompt" predicate
 * (L-6, 2026-08-07). Used IDENTICALLY by the player Address save gate, the
 * GeoGate modal-trigger, and the GeoForcedModal submit so they can never
 * disagree. (The lockout bug: the save gate accepted GPS-only while the
 * GeoGate demanded all six fields, so a structured-only save still re-fired
 * the non-dismissible modal forever.) True when EITHER precise GPS is set,
 * OR a full state+district+area triple, OR a pincode — GPS stays the
 * PREFERRED path in the UI but is never a hard requirement. Accepts a User
 * (lat/lng as string|number|null) or a GeoBag (number|null).
 */
export function isGeoLocated(g: {
  latitude?: number | string | null; longitude?: number | string | null;
  state_id?: number | null; district_id?: number | null;
  area_id?: number | null; pincode_id?: number | null;
} | null | undefined): boolean {
  if (!g) return false;
  if (g.latitude != null && g.longitude != null) return true;
  if (g.state_id != null && g.district_id != null && g.area_id != null) return true;
  if (g.pincode_id != null) return true;
  return false;
}

type State    = { id: number; name: string };
type District = { id: number; state_id: number; name: string };
type Area     = { id: number; district_id: number; name: string;
                  latitude: string | null; longitude: string | null };
type PincodeMatch = {
  pincode_id: number; pincode: string;
  area_id: number;    area_name: string;
  area_latitude: string | null; area_longitude: string | null;
  district_id: number; district_name: string;
  state_id: number;    state_name: string;
};

// Server-side response of POST /api/geo/resolve { latitude, longitude }.
type GpsResolveResult = {
  area_id: number;
  state_id: number;
  district_id: number;
  pincode_id?: number | null;
  nation?: string;
  area_name: string;
  district_name: string;
  state_name: string;
};

export type LocationCaptureMode = 'self' | 'child';

type Props = {
  value: GeoBag;
  onChange: (geo: GeoBag) => void;
  /**
   * 'self'  — user is setting their OWN location: pincode → GPS →
   *           15-km Haversine check. GPS is required. DEFAULT.
   * 'child' — user is setting somebody ELSE's location (admin creates
   *           agent, agent creates sub-agent, etc.). Pincode only,
   *           GPS button hidden, no Haversine check.
   */
  mode?: LocationCaptureMode;
  /**
   * When `true` (default) AND `value` already carries a resolved
   * location (full state/district/area triple OR a lat/lng pair),
   * the component opens in a compact read-only summary with an
   * "Edit location" button instead of dropping straight into the
   * editor. Pass `false` to force the editor unconditionally
   * (signup pages with no pre-existing value typically don't bother
   * because the value is empty anyway, but `false` is explicit).
   */
  showSavedSummary?: boolean;
  required?: boolean;   // visual hint only; parent enforces
  disabled?: boolean;
  /**
   * @deprecated Use `mode='child'` instead. Kept for back-compat with
   * the wave of existing call sites (admin/agent creator forms). If
   * BOTH `mode` and `hideGps` are passed, `mode` wins.
   *
   * Originally added in Phase R4 (creator-mode): hides the
   * "Use my current location" button. Used by admin/agent
   * "create-agent" + "create-sub-agent" forms where the creator is
   * filling in the LOCATION OF SOMEONE ELSE — capturing the
   * creator's own GPS would attribute the new agent to the creator's
   * office. The creator picks the new agent's rough area via pincode
   * lookup or the manual cascade instead; the new agent themselves
   * later overlays precise GPS via the GeoForcedModal on first login.
   */
  hideGps?: boolean;
  /**
   * Task B (2026-05-10): override the className applied to every
   * <input> and <select> rendered by this component, so the
   * pincode field and the State/District/Area dropdowns can adopt
   * the consumer page's input look (brand-themed in the player end,
   * neutral in admin/agent).
   *
   * When omitted, falls back to the historical hardcoded styling
   * so admin/agent surfaces (which were happy with the existing
   * neutral look) keep their current appearance unchanged.
   *
   * Player Profile.tsx passes `"input"` (the player-frontend's
   * Tailwind utility class — see player-frontend/src/index.css)
   * so the geo cascade matches the rest of the player profile's
   * inputs (rounded-xl, neon focus ring, larger px/py, text-base).
   */
  inputClassName?: string;
  /**
   * 2026-08-08 (UX) — when true, the read-only saved-location summary
   * omits its own "Edit location" button. Used when the summary lives
   * INSIDE a larger card that already owns a single Edit button (e.g.
   * the player's unified "Personal details" card), so there is exactly
   * one edit affordance for the whole card, not a competing per-field one.
   * No effect on the editor; default false = unchanged behaviour.
   */
  hideSummaryEdit?: boolean;
  /**
   * 2026-05-21 — Player signup-wizard layout flag.
   *
   * When true (only meaningful in `mode='self'`), the component hides
   * every section EXCEPT the "Use my current location" GPS button.
   * The pincode input, multi-area picker, validation banner, purpose
   * explainer, and the manual-cascade fallback are all suppressed so
   * the visual priority lands on the GPS button.
   *
   * The parent is expected to render its own "use a different method"
   * toggle and re-mount this component with `gpsFirstOnly={false}` to
   * surface the alternative inputs when the player wants them.
   *
   * Self-mode only — child / GeoForcedModal flows ignore this prop
   * since they have their own layout requirements.
   */
  gpsFirstOnly?: boolean;
  /**
   * 2026-05-21 — Suppress the amber "geo.purpose" banner at the top
   * of the editor. Used by parents that supply their OWN framing /
   * intro card above the component (e.g. player Register step 3,
   * which renders a themed "Where are you playing from?" card). The
   * banner duplicates that copy and adds vertical noise on mobile.
   *
   * Falls back to the default visible behaviour so admin / agent
   * surfaces that rely on the built-in intro keep showing it.
   */
  hidePurposeBanner?: boolean;
  /**
   * 2026-08-06 — fired when a GPS capture attempt fails (permission
   * denied or any geolocation error). Lets a `gpsFirstOnly` parent
   * (player signup step 3) auto-reveal its alternative-location panel
   * so a player who refuses GPS isn't left staring at a dead button.
   * Optional — existing consumers that don't pass it are unaffected.
   */
  onGpsDenied?: () => void;
};

// Default className used when the consumer doesn't pass `inputClassName`.
// Identical to the original hardcoded string so admin/agent pages
// — which never provide an override — render byte-for-byte the same
// as before this commit. Mirrored as a constant so accidental drift
// between the input and the four selects is impossible.
const DEFAULT_INPUT_CLASS =
  'w-full px-3 py-2 rounded-lg bg-white dark:bg-neutral-900 ' +
  'border border-neutral-300 dark:border-neutral-700 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-fuchsia-500';
// The select default omits the focus ring (selects don't get the
// fuchsia ring in the original styling — only the pincode input did).
// Keeping the parallel structure here so an override applies
// uniformly across all five fields.
const DEFAULT_SELECT_CLASS =
  'w-full px-3 py-2 rounded-lg bg-white dark:bg-neutral-900 ' +
  'border border-neutral-300 dark:border-neutral-700 text-sm';

// ─── Helpers ───────────────────────────────────────────────────────

// Haversine great-circle distance in kilometres. Earth radius 6371 km.
// Inputs in degrees; returns kilometres.
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// 15-km threshold for "your GPS lines up with the pincode you typed".
// Beyond this we trust GPS over the pincode (different city, the user
// mistyped or moved).
const SELF_MODE_MAX_KM = 15;

// Does this bag have enough to render the read-only summary?
// We accept either a full state+district+area triple OR a lat/lng pair.
function hasSavedLocation(g: GeoBag | null | undefined): boolean {
  if (!g) return false;
  if (g.latitude != null && g.longitude != null) return true;
  if (g.state_id != null && g.district_id != null && g.area_id != null) return true;
  return false;
}

// ─── Component ─────────────────────────────────────────────────────

export default function LocationCapture({
  value,
  onChange,
  mode,
  showSavedSummary = true,
  required,
  disabled,
  hideGps,
  hideSummaryEdit,
  inputClassName,
  gpsFirstOnly = false,
  hidePurposeBanner = false,
  onGpsDenied,
}: Props) {
  const t = useT();

  // Resolve effective mode. `mode` wins; if not passed, `hideGps`
  // back-compat shim maps to 'child'; otherwise default to 'self'.
  const effectiveMode: LocationCaptureMode =
    mode ?? (hideGps ? 'child' : 'self');
  const gpsHidden = effectiveMode === 'child';

  // Whether the summary card should be shown. Computed from props +
  // local "user clicked Edit" state. When the summary is shown, the
  // editor is hidden, and vice-versa.
  const initiallySaved = showSavedSummary && hasSavedLocation(value);
  const [editing, setEditing] = useState<boolean>(!initiallySaved);

  // Track the most recent value we emitted ourselves. When the
  // consumer passes a value that differs from this snapshot AND
  // differs from the previous render's value-prop, that's the
  // signature of a "save round trip completed": the parent saved
  // to the backend, refetched, and re-passed a fresh `value`.
  // In that case we flip back to view mode automatically (the
  // "two modes" UX: edit shows everything; press Save → snap back
  // to view-only summary).
  const lastEmittedRef = useRef<string>('');
  const lastValueRef   = useRef<string>('');

  // If the parent swaps to a fresh empty value (e.g., after a Reset
  // upstream), or hydrates a saved one after async load, keep the
  // summary/editor state in sync. We also detect save-completed
  // reseed (value changed from outside the component) and exit
  // edit mode in that case.
  useEffect(() => {
    if (!showSavedSummary) {
      setEditing(true);
      return;
    }
    if (!hasSavedLocation(value)) {
      setEditing(true);
      return;
    }
    // Compare current value's geo signature to what we last saw.
    const currentSig = JSON.stringify({
      lat: value.latitude    ?? null,
      lng: value.longitude   ?? null,
      s:   value.state_id    ?? null,
      d:   value.district_id ?? null,
      a:   value.area_id     ?? null,
      p:   value.pincode_id  ?? null,
    });
    const externalChange =
      currentSig !== lastValueRef.current && currentSig !== lastEmittedRef.current;
    lastValueRef.current = currentSig;
    if (externalChange && editing) {
      // Parent re-hydrated `value` (post-save data refresh). Flip to
      // view mode so the user sees the clean saved-state summary.
      setEditing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSavedSummary, hasSavedLocation(value),
      value.latitude, value.longitude, value.state_id,
      value.district_id, value.area_id, value.pincode_id]);

  // ── UI state (component-local; not in the geo bag) ──
  const [pincodeInput, setPincodeInput]   = useState('');
  const [pincodeMatches, setPincodeMatches] = useState<PincodeMatch[]>([]);
  const [pincodeError, setPincodeError]   = useState<string | null>(null);
  const [gpsBusy, setGpsBusy]             = useState(false);
  const [gpsError, setGpsError]           = useState<string | null>(null);
  // Cached geo-tree dropdown data — fetched lazily as the user
  // descends the cascade, so we never load all 162k areas upfront.
  const [states, setStates]               = useState<State[]>([]);
  const [districts, setDistricts]         = useState<District[]>([]);
  const [areas, setAreas]                 = useState<Area[]>([]);
  // Names resolved from GPS/pincode (read-only display so the user
  // can confirm "yes, that's me"). Cleared when user resets.
  const [resolvedLabel, setResolvedLabel] = useState<string | null>(null);
  // Self-mode only: after the pincode resolves, we remember its area
  // lat/lng so the GPS step can run the Haversine check without
  // another fetch. Cleared whenever the pincode input changes.
  const [pinAnchor, setPinAnchor] = useState<{
    lat: number; lng: number;
    pin_area_id: number;
    state_id: number; district_id: number; pincode_id: number;
    area_name: string; district_name: string; state_name: string;
  } | null>(null);
  // Self-mode display banner about the 15-km test outcome.
  const [validationNote, setValidationNote] = useState<string | null>(null);
  // Which source won the 15-km Haversine race in self mode. Drives the
  // source-priority chip on the saved-summary card. Persists across an
  // editor → summary transition (we don't clear it just because the
  // editor collapsed). Reset only when the user clicks Edit + Change.
  //   'pin'  → pincode area saved, GPS confirmed within 15 km
  //   'gps'  → GPS area saved, pincode was overridden as too far
  //   null   → child-mode, or self-mode without a completed Haversine
  //            race (e.g. user GPS-locked but typed no pincode).
  const [sourcePriority, setSourcePriority] =
    useState<'pin' | 'gps' | null>(null);
  // Bug A v2 (2026-05-13): the original "dirty flag flips on any
  // keystroke" approach failed because the HYDRATED value already
  // satisfied the auto-collapse READY predicate (area_id + pincode_id
  // populated from the parent record). One keystroke flipped dirty
  // → ready=true off the hydration → 1.5-s timer fired → editor
  // closed before the user could finish typing. The robust fix:
  // snapshot the bag at the moment the editor opens, then track
  // dirty as "the bag's completion-relevant fields ACTUALLY changed
  // from the snapshot." That way auto-collapse only fires when the
  // user has materially advanced the state — never off the hydrated
  // baseline alone.
  //
  // The snapshot is recaptured every time the editor transitions
  // false → true (i.e. Edit button click, fresh mount in editor
  // mode, etc.) via a `useEffect` watching `editing`.
  type DirtySnapshot = {
    latitude:    number | null;
    longitude:   number | null;
    state_id:    number | null;
    district_id: number | null;
    area_id:     number | null;
    pincode_id:  number | null;
  };
  const emptySnapshot: DirtySnapshot = {
    latitude: null, longitude: null,
    state_id: null, district_id: null,
    area_id:  null, pincode_id:  null,
  };
  const [editOpenSnapshot, setEditOpenSnapshot] = useState<DirtySnapshot>(emptySnapshot);
  // Cache mapping pincode_id → human-readable pincode string. Populated
  // whenever we receive a successful /api/geo/pincode-lookup response
  // during the editor session. There is no backend endpoint to fetch
  // a pincode STRING by id, so the summary can only display the
  // pincode if the user typed it earlier in THIS session — degrades
  // gracefully (we just omit the pincode line) on a cold reload where
  // the user hasn't opened the editor yet.
  const [pincodeCache, setPincodeCache] =
    useState<Record<number, string>>({});
  // Cache of resolved state / district / area names, indexed by id.
  // Populated on-demand by the lazy summary-name-resolution effect
  // below so the summary card can render "AREA · DISTRICT · STATE"
  // even when the editor was never opened. Mirrors what the prior
  // AccountPanel `GeoNames` fetch did, but lives inside the
  // component so consumers don't have to do it.
  const [nameCache, setNameCache] = useState<{
    states:    Record<number, string>;
    districts: Record<number, string>;
    areas:     Record<number, string>;
  }>({ states: {}, districts: {}, areas: {} });

  // ── Initial state load (only when manual cascade becomes visible) ──
  // Optimization: don't fetch /api/geo/states until the user actually
  // expands the manual section. Saves a round-trip on the GPS path.
  const [manualOpen, setManualOpen]       = useState(false);
  // Manual-cascade pincode options. Populated by /api/geo/areas/:id/pincodes
  // after the user picks an area via the manual cascade. When the
  // area has multiple pincodes, this drives a small picker dropdown
  // rendered ABOVE the cascade. Cleared whenever the user picks a
  // different area or resets.
  type AreaPincode = { pincode_id: number; pincode: string };
  const [cascadePincodeOptions, setCascadePincodeOptions] =
    useState<AreaPincode[]>([]);
  useEffect(() => {
    if (manualOpen && states.length === 0) {
      fetch('/api/geo/states')
        .then((r) => r.json())
        .then((j) => { if (j.ok) setStates(j.data); })
        .catch(() => { /* silent — manual flow degrades gracefully */ });
    }
  }, [manualOpen, states.length]);

  // ── Helper: emit a partial-or-full geo bag to the parent ──
  // We always include `nation: 'India'` as the floor — even if the
  // user provides nothing else, the server-side cascade can fall to
  // L5 with nation='India'.
  const emit = useCallback((partial: Partial<GeoBag>, label?: string | null) => {
    const next = {
      latitude:    partial.latitude    ?? null,
      longitude:   partial.longitude   ?? null,
      state_id:    partial.state_id    ?? null,
      district_id: partial.district_id ?? null,
      area_id:     partial.area_id     ?? null,
      pincode_id:  partial.pincode_id  ?? null,
      nation:      partial.nation      ?? 'India',
      pin_area_id: partial.pin_area_id ?? null,
      gps_area_id: partial.gps_area_id ?? null,
      pincode:     partial.pincode     ?? null,
    };
    // Record our emit signature so the external-reseed detector
    // (in the editing useEffect) can distinguish our own update
    // from a save-then-refetch reseed by the parent. The signature
    // covers the same six fields the detector uses.
    lastEmittedRef.current = JSON.stringify({
      lat: next.latitude    ?? null,
      lng: next.longitude   ?? null,
      s:   next.state_id    ?? null,
      d:   next.district_id ?? null,
      a:   next.area_id     ?? null,
      p:   next.pincode_id  ?? null,
    });
    onChange(next);
    if (label !== undefined) setResolvedLabel(label);
  }, [onChange]);

  // ─────────────────────────────────────────────────────────────────
  // PINCODE LOOKUP (debounced) — Step 1 in self-mode, the whole show
  // in child-mode.
  // ─────────────────────────────────────────────────────────────────
  // Re-runs ~400ms after the user stops typing. Avoids hammering the
  // endpoint on every keystroke. Min 5 chars before the first call
  // — prevents lookups on partial input (Indian pincodes are 6 chars).
  useEffect(() => {
    setPincodeError(null);
    setValidationNote(null);
    if (pincodeInput.length < 5) {
      setPincodeMatches([]);
      setPinAnchor(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/geo/pincode-lookup?pincode=${encodeURIComponent(pincodeInput)}`,
        );
        const j = await res.json();
        if (!j.ok) {
          setPincodeError(t('geo.pincode.lookupFailed'));
          return;
        }
        setPincodeMatches(j.data || []);
        if (!j.data || j.data.length === 0) {
          setPincodeError(t('geo.pincode.notFound'));
          setPinAnchor(null);
          return;
        }
        // Populate the pincode_id → string cache from every match.
        // Used by the saved-summary card to display "Pin code: 400001"
        // even after the editor collapses. We also cache state /
        // district / area names so the summary doesn't have to re-
        // fetch them lazily on the next collapse.
        setPincodeCache((c) => {
          const next = { ...c };
          for (const m of j.data as PincodeMatch[]) {
            next[m.pincode_id] = m.pincode;
          }
          return next;
        });
        setNameCache((c) => {
          const ns = { ...c.states };
          const nd = { ...c.districts };
          const na = { ...c.areas };
          for (const m of j.data as PincodeMatch[]) {
            ns[m.state_id]    = m.state_name;
            nd[m.district_id] = m.district_name;
            na[m.area_id]     = m.area_name;
          }
          return { states: ns, districts: nd, areas: na };
        });
        if (j.data.length === 1) {
          // Single-area pincode — auto-fill, no picker needed.
          const r = j.data[0] as PincodeMatch;
          const lat = r.area_latitude  ? Number(r.area_latitude)  : null;
          const lng = r.area_longitude ? Number(r.area_longitude) : null;
          if (effectiveMode === 'self') {
            // Don't emit lat/lng yet — those slots are reserved for
            // the device GPS reading in self mode. Stash the area's
            // own lat/lng on the pin anchor so we can run the
            // Haversine check after GPS resolves.
            emit({
              state_id:    r.state_id,
              district_id: r.district_id,
              area_id:     r.area_id,
              pincode_id:  r.pincode_id,
              pin_area_id: r.area_id,
              pincode:     r.pincode,
            }, `${r.area_name} · ${r.district_name} · ${r.state_name}`);
            if (lat != null && lng != null) {
              setPinAnchor({
                lat, lng,
                pin_area_id: r.area_id,
                state_id: r.state_id, district_id: r.district_id,
                pincode_id: r.pincode_id,
                area_name: r.area_name,
                district_name: r.district_name,
                state_name: r.state_name,
              });
            } else {
              // Pincode resolved but has no stored lat/lng — without
              // an anchor we can't run the 15-km test. We still let
              // the user GPS-lock; the GPS-resolved area will simply
              // be saved alongside (treated as if d > 15 km since we
              // can't prove proximity).
              setPinAnchor(null);
            }
          } else {
            // Child mode — pincode IS the answer; store area's
            // lat/lng directly so the server's L1 fallback can hit
            // even though no device GPS was used.
            emit({
              state_id:    r.state_id,
              district_id: r.district_id,
              area_id:     r.area_id,
              pincode_id:  r.pincode_id,
              latitude:    lat,
              longitude:   lng,
              pin_area_id: r.area_id,
              pincode:     r.pincode,
            }, `${r.area_name} · ${r.district_name} · ${r.state_name}`);
          }
        }
        // If j.data.length > 1, the multi-area dropdown below
        // renders for the user to pick. UX improvement (2026-05-13):
        // if the saved area_id is already among the matches (parent
        // re-typed the same pincode their child already has), set
        // resolvedLabel for that match so the green confirmation
        // pill appears. Communicates "your typed pincode maps to
        // the saved area" without forcing the user to click the
        // pre-selected dropdown option.
        if (j.data.length > 1 && value.area_id != null) {
          const matched = (j.data as PincodeMatch[]).find(
            (m) => m.area_id === value.area_id,
          );
          if (matched) {
            setResolvedLabel(
              `${matched.area_name} · ${matched.district_name} · ${matched.state_name}`,
            );
          }
        }
      } catch {
        setPincodeError(t('geo.pincode.lookupFailed'));
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [pincodeInput, emit, t, effectiveMode]);

  // Snapshot the bag at the moment the editor opens. We compare the
  // current value against this snapshot to decide "did the user
  // materially change something?" — the precondition for the auto-
  // collapse timer. Re-runs every false → true transition of
  // `editing` so each Edit click gets a fresh baseline.
  useEffect(() => {
    if (!editing) return;
    setEditOpenSnapshot({
      latitude:    value.latitude    ?? null,
      longitude:   value.longitude   ?? null,
      state_id:    value.state_id    ?? null,
      district_id: value.district_id ?? null,
      area_id:     value.area_id     ?? null,
      pincode_id:  value.pincode_id  ?? null,
    });
    // We deliberately only sync at the open transition. Subsequent
    // value changes are what we MEASURE against this snapshot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // True iff the user materially advanced the bag from the
  // baseline captured when the editor opened. This is the gate the
  // auto-collapse predicate uses — guarantees the timer can never
  // fire off the hydrated value alone.
  const editorDirty = useMemo(() => {
    return (
      (value.latitude    ?? null) !== editOpenSnapshot.latitude    ||
      (value.longitude   ?? null) !== editOpenSnapshot.longitude   ||
      (value.state_id    ?? null) !== editOpenSnapshot.state_id    ||
      (value.district_id ?? null) !== editOpenSnapshot.district_id ||
      (value.area_id     ?? null) !== editOpenSnapshot.area_id     ||
      (value.pincode_id  ?? null) !== editOpenSnapshot.pincode_id
    );
  }, [
    value.latitude, value.longitude,
    value.state_id, value.district_id, value.area_id, value.pincode_id,
    editOpenSnapshot,
  ]);

  // Multi-area picker change handler. Used only when pincodeMatches.length > 1.
  const handlePincodeAreaPick = useCallback((areaId: number) => {
    const r = pincodeMatches.find((m) => m.area_id === areaId);
    if (!r) return;
    const lat = r.area_latitude  ? Number(r.area_latitude)  : null;
    const lng = r.area_longitude ? Number(r.area_longitude) : null;
    if (effectiveMode === 'self') {
      emit({
        state_id:    r.state_id,
        district_id: r.district_id,
        area_id:     r.area_id,
        pincode_id:  r.pincode_id,
        pin_area_id: r.area_id,
        pincode:     r.pincode,
      }, `${r.area_name} · ${r.district_name} · ${r.state_name}`);
      if (lat != null && lng != null) {
        setPinAnchor({
          lat, lng,
          pin_area_id: r.area_id,
          state_id: r.state_id, district_id: r.district_id,
          pincode_id: r.pincode_id,
          area_name: r.area_name,
          district_name: r.district_name,
          state_name: r.state_name,
        });
      } else {
        setPinAnchor(null);
      }
    } else {
      emit({
        state_id:    r.state_id,
        district_id: r.district_id,
        area_id:     r.area_id,
        pincode_id:  r.pincode_id,
        latitude:    lat,
        longitude:   lng,
        pin_area_id: r.area_id,
        pincode:     r.pincode,
      }, `${r.area_name} · ${r.district_name} · ${r.state_name}`);
    }
  }, [pincodeMatches, emit, effectiveMode]);

  // ─────────────────────────────────────────────────────────────────
  // GPS LOCK (self-mode step 2) + 15-km Haversine validation
  // ─────────────────────────────────────────────────────────────────
  const handleGps = useCallback(async () => {
    setGpsError(null);
    setGpsBusy(true);
    setValidationNote(null);
    try {
      // Wrap navigator.geolocation in a Promise so we can await it.
      // Browser-level permission prompt fires here on first call.
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('GPS not supported on this device'));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10_000,
          maximumAge: 60_000,
        });
      });
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;

      // Always reverse-resolve the GPS coords. The result will either
      // back up the pincode (15-km test passes) or override it.
      const res = await fetch('/api/geo/resolve', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ latitude: lat, longitude: lng }),
      });
      const j = await res.json();
      const gpsResolved: GpsResolveResult | null = j.ok && j.data ? j.data : null;

      // Derive a Haversine anchor if the user reached `area_id` via
      // the manual cascade instead of the pincode lookup. The cascade
      // Area object carries lat/lng from /api/geo/areas?district_id=N,
      // so we can run the same 15-km test. Names come from the
      // in-component caches (populated by handleAreaPick et al.).
      let activeAnchor = pinAnchor;
      if (!activeAnchor && value.area_id != null) {
        const cascadeArea = areas.find((x) => x.id === value.area_id);
        const cLat = cascadeArea?.latitude  ? Number(cascadeArea.latitude)  : null;
        const cLng = cascadeArea?.longitude ? Number(cascadeArea.longitude) : null;
        if (
          cascadeArea && cLat != null && cLng != null &&
          value.state_id != null && value.district_id != null
        ) {
          activeAnchor = {
            lat: cLat, lng: cLng,
            pin_area_id: cascadeArea.id,
            state_id:    value.state_id,
            district_id: value.district_id,
            // Manual cascade may not have a pincode_id yet (the area→
            // pincodes call could have returned zero or be in-flight),
            // but the Haversine math only needs the anchor lat/lng.
            // Fall back to whatever's on the bag (0 if absent — harmless
            // since pincode_id is forwarded explicitly below).
            pincode_id:  value.pincode_id ?? 0,
            area_name:     nameCache.areas[cascadeArea.id]      ?? cascadeArea.name,
            district_name: nameCache.districts[value.district_id] ?? '',
            state_name:    nameCache.states[value.state_id]       ?? '',
          };
        }
      }

      // Branch A: we have an anchor (pincode lookup OR manual
      // cascade) — run the Haversine check.
      if (activeAnchor) {
        const distanceKm = haversineKm(activeAnchor.lat, activeAnchor.lng, lat, lng);
        // Pull the human-readable pincode string from the in-component
        // cache if we have it (we populated it during pincode-lookup);
        // forward it through `emit` so the saved-summary card can show
        // "Pin code: NNNNNN" without a second round-trip.
        const pinStr = pincodeCache[activeAnchor.pincode_id] ?? value.pincode ?? null;
        // pincode_id of 0 is our sentinel for "manual cascade with no
        // resolved pincode yet" — don't forward it as a real FK.
        const anchorPincodeId = activeAnchor.pincode_id || null;
        if (distanceKm <= SELF_MODE_MAX_KM) {
          // PIN WINS — keep the pincode-resolved area as the primary.
          emit({
            latitude:    lat,
            longitude:   lng,
            state_id:    activeAnchor.state_id,
            district_id: activeAnchor.district_id,
            area_id:     activeAnchor.pin_area_id,
            pincode_id:  anchorPincodeId,
            pin_area_id: activeAnchor.pin_area_id,
            gps_area_id: gpsResolved?.area_id ?? null,
            pincode:     pinStr,
          }, `${activeAnchor.area_name} · ${activeAnchor.district_name} · ${activeAnchor.state_name}`);
          setValidationNote(t('geo.validation.matched'));
          setSourcePriority('pin');
          return;
        }
        // GPS WINS — fall through to the override below.
        if (!gpsResolved) {
          // GPS too far from pin AND we couldn't reverse-resolve the
          // GPS coords. Keep the pincode area as the saved area (best
          // we can do) but stash the GPS lat/lng for the server's
          // radius search to use directly.
          emit({
            latitude:    lat,
            longitude:   lng,
            state_id:    activeAnchor.state_id,
            district_id: activeAnchor.district_id,
            area_id:     activeAnchor.pin_area_id,
            pincode_id:  anchorPincodeId,
            pin_area_id: activeAnchor.pin_area_id,
            gps_area_id: null,
            pincode:     pinStr,
          }, `${activeAnchor.area_name} · ${activeAnchor.district_name} · ${activeAnchor.state_name}`);
          setGpsError(t('geo.gps.notResolved'));
          return;
        }
        emit({
          latitude:    lat,
          longitude:   lng,
          state_id:    gpsResolved.state_id,
          district_id: gpsResolved.district_id,
          area_id:     gpsResolved.area_id,
          pincode_id:  gpsResolved.pincode_id ?? null,
          pin_area_id: activeAnchor.pin_area_id,
          gps_area_id: gpsResolved.area_id,
          // GPS-resolved pincode_id has no string available locally;
          // the lazy summary effect will fetch it on demand. Forward
          // any pincode string we already had only if the FK matches.
          pincode:     gpsResolved.pincode_id === anchorPincodeId ? pinStr : null,
        }, `${gpsResolved.area_name} · ${gpsResolved.district_name} · ${gpsResolved.state_name}`);
        // Cache the GPS-side names so the summary card can still
        // display them after the editor collapses.
        setNameCache((c) => ({
          states:    { ...c.states,    [gpsResolved.state_id]:    gpsResolved.state_name },
          districts: { ...c.districts, [gpsResolved.district_id]: gpsResolved.district_name },
          areas:     { ...c.areas,     [gpsResolved.area_id]:     gpsResolved.area_name },
        }));
        setValidationNote(t('geo.validation.gpsOverride'));
        setSourcePriority('gps');
        return;
      }

      // Branch B: no pin anchor (user hasn't typed a pincode yet, or
      // the pincode-resolved area lacked lat/lng). Just use the GPS
      // resolution as a standalone — equivalent to the legacy Flow 1.
      if (!gpsResolved) {
        setGpsError(t('geo.gps.notResolved'));
        return;
      }
      emit({
        latitude:    lat,
        longitude:   lng,
        state_id:    gpsResolved.state_id,
        district_id: gpsResolved.district_id,
        area_id:     gpsResolved.area_id,
        pincode_id:  gpsResolved.pincode_id ?? null,
        gps_area_id: gpsResolved.area_id,
        // No pin-side string to forward; lazy effect handles fetch.
        pincode:     null,
      }, `${gpsResolved.area_name} · ${gpsResolved.district_name} · ${gpsResolved.state_name}`);
      // No pincode anchor → no priority race; this is a standalone
      // GPS save. Leave sourcePriority null so the chip is hidden.
      setNameCache((c) => ({
        states:    { ...c.states,    [gpsResolved.state_id]:    gpsResolved.state_name },
        districts: { ...c.districts, [gpsResolved.district_id]: gpsResolved.district_name },
        areas:     { ...c.areas,     [gpsResolved.area_id]:     gpsResolved.area_name },
      }));
    } catch (e: unknown) {
      const err = e as { code?: number; message?: string };
      // PERMISSION_DENIED = 1 (user said no). Friendly fallback.
      if (err.code === 1) setGpsError(t('geo.gps.denied'));
      else                setGpsError(t('geo.gps.failed'));
      // Signal the parent so a gpsFirstOnly signup can auto-open its
      // alternative-location panel — a denied/failed GPS should never
      // dead-end the player.
      onGpsDenied?.();
    } finally {
      setGpsBusy(false);
    }
  }, [
    emit, t, pinAnchor, pincodeCache,
    value.pincode, value.area_id, value.state_id, value.district_id, value.pincode_id,
    areas, nameCache, onGpsDenied,
  ]);

  // ─────────────────────────────────────────────────────────────────
  // MANUAL CASCADE (fallback only — pincode unknown / lookup failed)
  // ─────────────────────────────────────────────────────────────────
  // Each dropdown change triggers the next-level fetch + emits.
  // Note: state-only / district-only selections still emit a partial
  // bag — the server-side cascade can route at L4 (state) or L5
  // (nation). User isn't forced to drill all the way to area.
  const handleStatePick = useCallback(async (sid: number) => {
    setDistricts([]); setAreas([]);
    const s = states.find((x) => x.id === sid);
    if (s) {
      setNameCache((c) => ({ ...c, states: { ...c.states, [sid]: s.name } }));
    }
    emit({ state_id: sid });
    try {
      const j = await fetch(`/api/geo/districts?state_id=${sid}`).then((r) => r.json());
      if (j.ok) setDistricts(j.data);
    } catch { /* dropdown stays empty; non-fatal */ }
  }, [emit, states]);

  const handleDistrictPick = useCallback(async (did: number) => {
    setAreas([]);
    const d = districts.find((x) => x.id === did);
    if (d) {
      setNameCache((c) => ({ ...c, districts: { ...c.districts, [did]: d.name } }));
    }
    emit({ state_id: value.state_id, district_id: did });
    try {
      const j = await fetch(`/api/geo/areas?district_id=${did}`).then((r) => r.json());
      if (j.ok) setAreas(j.data);
    } catch { /* non-fatal */ }
  }, [emit, value.state_id, districts]);

  const handleAreaPick = useCallback(async (aid: number) => {
    const a = areas.find((x) => x.id === aid);
    if (a) {
      setNameCache((c) => ({ ...c, areas: { ...c.areas, [aid]: a.name } }));
    }
    // Compose a human-readable confirmation label from the in-component
    // name caches. Resolve names with sensible fallbacks so the pill
    // never shows "undefined". Cached names come from prior cascade
    // picks (state/district set their own cache entries above).
    const sName = value.state_id    != null ? nameCache.states[value.state_id]       ?? '' : '';
    const dName = value.district_id != null ? nameCache.districts[value.district_id] ?? '' : '';
    const aName = a?.name ?? (nameCache.areas[aid] ?? '');
    const label = [aName, dName, sName].filter(Boolean).join(' · ') || null;
    // Emit immediately so the GPS gate (which now triggers on
    // area_id, not pincode_id) unlocks even before the pincode
    // lookup returns. We may amend with pincode_id below.
    emit({
      state_id:    value.state_id,
      district_id: value.district_id,
      area_id:     aid,
      latitude:  a?.latitude  ? Number(a.latitude)  : null,
      longitude: a?.longitude ? Number(a.longitude) : null,
    }, label);
    // Reset any prior cascade-pincode picker state — the user
    // changed area, so any previous options are stale.
    setCascadePincodeOptions([]);
    // Resolve pincode(s) for this area. Auto-fill if exactly one,
    // show a picker if 2+, log a warning if zero.
    try {
      const j = await fetch(`/api/geo/areas/${aid}/pincodes`).then((r) => r.json());
      if (!j.ok) return;
      const list = (j.data || []) as AreaPincode[];
      if (list.length === 0) {
        // Broken/degraded data — area exists but has no pincode rows.
        // Log so the locator team can spot it; allow user to continue
        // without pincode_id (GPS gate already unlocked above).
        console.warn(`[LocationCapture] area ${aid} has no pincodes`);
        return;
      }
      // Cache strings unconditionally so the summary card can render
      // "Pin code: NNNNNN" regardless of which path picked it.
      setPincodeCache((c) => {
        const next = { ...c };
        for (const p of list) next[p.pincode_id] = p.pincode;
        return next;
      });
      if (list.length === 1) {
        // Single pincode — auto-fill. Emit the full bag including the
        // pincode_id and the human-readable string for the summary.
        // Append "· Pin {pincode}" to the confirmation label so the
        // user sees the auto-fill in the green pill (the previous
        // version completed silently — bug B).
        const only = list[0];
        const labelWithPin = label
          ? `${label} · Pin ${only.pincode}`
          : `Pin ${only.pincode}`;
        emit({
          state_id:    value.state_id,
          district_id: value.district_id,
          area_id:     aid,
          pincode_id:  only.pincode_id,
          pincode:     only.pincode,
          latitude:  a?.latitude  ? Number(a.latitude)  : null,
          longitude: a?.longitude ? Number(a.longitude) : null,
        }, labelWithPin);
        return;
      }
      // Multiple pincodes — surface the picker. Don't preselect; we
      // wait for the user to pick.
      setCascadePincodeOptions(list);
    } catch {
      // Network or parse error — non-fatal, the user keeps an
      // area_id-only bag (GPS gate still works).
    }
  }, [areas, emit, value.state_id, value.district_id, nameCache]);

  // Manual-cascade pincode picker change handler. Used only when
  // /api/geo/areas/:id/pincodes returned 2+ options. Emits the bag
  // with the chosen pincode_id and caches its string.
  const handleCascadePincodePick = useCallback((pid: number) => {
    const opt = cascadePincodeOptions.find((p) => p.pincode_id === pid);
    if (!opt) return;
    const a = value.area_id != null
      ? areas.find((x) => x.id === value.area_id) ?? null
      : null;
    // Build the confirmation label including the chosen pincode so the
    // green pill confirms exactly what was saved (matches the single-
    // pincode auto-fill path).
    const sName = value.state_id    != null ? nameCache.states[value.state_id]       ?? '' : '';
    const dName = value.district_id != null ? nameCache.districts[value.district_id] ?? '' : '';
    const aName = value.area_id     != null ? nameCache.areas[value.area_id]         ?? (a?.name ?? '') : '';
    const base  = [aName, dName, sName].filter(Boolean).join(' · ');
    const label = base ? `${base} · Pin ${opt.pincode}` : `Pin ${opt.pincode}`;
    emit({
      state_id:    value.state_id,
      district_id: value.district_id,
      area_id:     value.area_id,
      pincode_id:  opt.pincode_id,
      pincode:     opt.pincode,
      latitude:  a?.latitude  ? Number(a.latitude)  : null,
      longitude: a?.longitude ? Number(a.longitude) : null,
    }, label);
  }, [cascadePincodeOptions, emit, value.state_id, value.district_id, value.area_id, areas, nameCache]);

  const handleReset = useCallback(() => {
    setPincodeInput('');
    setPincodeMatches([]);
    setPincodeError(null);
    setGpsError(null);
    setResolvedLabel(null);
    setManualOpen(false);
    setDistricts([]); setAreas([]);
    setPinAnchor(null);
    setValidationNote(null);
    setSourcePriority(null);
    setCascadePincodeOptions([]);
    // User explicitly cleared everything — they're starting fresh in
    // the editor, so dirty stays true (any subsequent emit should be
    // allowed to auto-collapse). But for the first beat after a Change
    // click on the saved card, we don't want the predicate to fire
    // against the cleared bag (which is incomplete), so this is fine.
    onChange({ nation: 'India' });
  }, [onChange]);

  // Memoize "is something filled" so we can show the confirmation pill.
  const hasSomething = useMemo(
    () => value.area_id != null || value.district_id != null || value.state_id != null,
    [value.area_id, value.district_id, value.state_id],
  );

  // Compact "AREA · DISTRICT · STATE" summary line for the read-only
  // card. Prefers names from the in-component cache (populated by the
  // pincode-lookup, manual-cascade, and lazy-name-resolution effects).
  // Falls back to the freshly emitted `resolvedLabel` from the most
  // recent edit, and finally to null (the render falls back to the
  // generic "Saved location" string).
  const summaryLine = useMemo(() => {
    const areaName     = value.area_id     != null ? nameCache.areas[value.area_id]         : null;
    const districtName = value.district_id != null ? nameCache.districts[value.district_id] : null;
    const stateName    = value.state_id    != null ? nameCache.states[value.state_id]       : null;
    const parts = [areaName, districtName, stateName].filter(Boolean) as string[];
    if (parts.length > 0) return parts.join(' · ');
    if (resolvedLabel) return resolvedLabel;
    return null;
  }, [
    value.area_id, value.district_id, value.state_id,
    nameCache, resolvedLabel,
  ]);

  // Pincode STRING resolution. The GeoBag carries `pincode_id` (the
  // numeric FK) but the user wants to see the actual 6-digit pincode
  // string on the summary card. Three lookup tiers, fastest first:
  //
  //   1. `value.pincode` (caller hydrated the bag with the string
  //      directly — no fetch needed).
  //   2. `pincodeCache` (populated by /api/geo/pincode-lookup,
  //      /api/geo/areas/:id/pincodes, or the lazy effect below).
  //   3. fall back to null (the summary line is hidden while the
  //      lazy effect's /api/geo/pincode/:id request is in flight).
  const summaryPincode = useMemo<string | null>(() => {
    if (value.pincode_id == null) return null;
    if (value.pincode) return value.pincode;
    return pincodeCache[value.pincode_id] ?? null;
  }, [value.pincode_id, value.pincode, pincodeCache]);

  // ── Lazy name resolution for the summary card ───────────────────
  // When the component opens in summary mode (e.g. AdminUserDetail
  // first paint, AccountPanel after a refresh) and the GeoBag has
  // state_id/district_id/area_id BUT we haven't seen them in this
  // session, fetch the names so the summary can render
  // "AREA · DISTRICT · STATE" instead of just "Saved location".
  // Mirrors what the prior AccountPanel `GeoNames` fetch did, now
  // owned by the component so consumers don't have to.
  useEffect(() => {
    if (editing) return; // editor flow does its own name loading
    let cancelled = false;
    async function resolveNames() {
      // STATE
      if (value.state_id != null && nameCache.states[value.state_id] == null) {
        try {
          const j = await fetch('/api/geo/states').then((r) => r.json());
          if (cancelled || !j.ok) return;
          const map: Record<number, string> = {};
          for (const s of j.data as State[]) map[s.id] = s.name;
          setNameCache((c) => ({ ...c, states: { ...c.states, ...map } }));
        } catch { /* non-fatal — summary degrades to area-only */ }
      }
      if (cancelled) return;
      // DISTRICT
      if (value.state_id != null && value.district_id != null &&
          nameCache.districts[value.district_id] == null) {
        try {
          const j = await fetch(`/api/geo/districts?state_id=${value.state_id}`)
            .then((r) => r.json());
          if (cancelled || !j.ok) return;
          const map: Record<number, string> = {};
          for (const d of j.data as District[]) map[d.id] = d.name;
          setNameCache((c) => ({ ...c, districts: { ...c.districts, ...map } }));
        } catch { /* non-fatal */ }
      }
      if (cancelled) return;
      // AREA
      if (value.district_id != null && value.area_id != null &&
          nameCache.areas[value.area_id] == null) {
        try {
          const j = await fetch(`/api/geo/areas?district_id=${value.district_id}`)
            .then((r) => r.json());
          if (cancelled || !j.ok) return;
          const map: Record<number, string> = {};
          for (const a of j.data as Area[]) map[a.id] = a.name;
          setNameCache((c) => ({ ...c, areas: { ...c.areas, ...map } }));
        } catch { /* non-fatal */ }
      }
      if (cancelled) return;
      // PINCODE STRING — only fetch when:
      //   * the bag has a `pincode_id` (FK present), AND
      //   * the consumer DIDN'T hydrate `value.pincode` directly, AND
      //   * the in-session cache doesn't already have the string.
      // The fast path (consumer-provided `value.pincode`) avoids an
      // extra API call on every page load for backends that return
      // both the FK and the string in their user/agent fetch.
      if (
        value.pincode_id != null &&
        !value.pincode &&
        pincodeCache[value.pincode_id] == null
      ) {
        try {
          const j = await fetch(`/api/geo/pincode/${value.pincode_id}`)
            .then((r) => r.json());
          if (cancelled || !j.ok || !j.data) return;
          const pid = j.data.pincode_id as number;
          const pstr = j.data.pincode as string;
          if (pid != null && pstr != null) {
            setPincodeCache((c) => ({ ...c, [pid]: pstr }));
          }
        } catch { /* non-fatal — summary line stays hidden */ }
      }
    }
    resolveNames();
    return () => { cancelled = true; };
  // We deliberately exclude `nameCache` and `pincodeCache` from the
  // deps — the effect is gated on "summary mode AND data missing"
  // and we don't want to re-run every time a cache itself updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, value.state_id, value.district_id, value.area_id,
      value.pincode_id, value.pincode]);

  // ── Auto-collapse REMOVED (2026-05-13) ──────────────────────────
  // The previous implementation tried to auto-collapse the editor
  // back to summary view 1.5 s after the user completed a flow. It
  // was buggy in practice: after a real-typed pincode resolves
  // (~400 ms debounce + ~200 ms API roundtrip = ~600 ms), the bag
  // changes → editorDirty flips true → the 1.5 s timer fires →
  // editor closes ~2 s after the user stopped typing. From the
  // user's perspective the form vanished under them mid-thought.
  //
  // Now: the editor stays open until the user explicitly clicks
  // "Change" on the green resolved-label pill (which calls handleReset
  // to clear the bag), OR the consumer page navigates / refetches
  // and the component remounts. The green pill + the parent form's
  // Save button provide all the visible confirmation the user needs;
  // no automated transition is required.
  //
  // `editorDirty` (and the `editOpenSnapshot`) are kept as state for
  // potential future use, but no consumer reads them today.
  // (No effect — auto-collapse intentionally absent.)

  // Self-mode GPS gate. Triggers when the user has resolved an area
  // by ANY path (pincode lookup, manual cascade, or a hydrated saved
  // value). The Haversine 15-km check downstream uses whichever area
  // has lat/lng resolved — `pinAnchor` for the pincode path, the
  // `areas` array's stored lat/lng for the manual-cascade path — so
  // the same comparison logic works regardless of how `area_id` was
  // produced.
  //
  // Historical note: this gate originally required `pincode_id` to
  // be set. That gated out users who fell through to the manual
  // cascade fallback (which didn't fill `pincode_id`), leaving them
  // stranded on the "Now lock your GPS" hint. Now we trigger on
  // `area_id` alone so the manual cascade path can complete.
  const selfPincodeResolved = effectiveMode === 'self' && (
    pinAnchor != null || value.area_id != null
  );

  // ─── Render ──────────────────────────────────────────────────────

  // ── Summary card (read-only) ─────────────────────────────────────
  // Shown when:
  //   - `showSavedSummary` is true (default), AND
  //   - the incoming value carries a saved location, AND
  //   - the user hasn't clicked Edit yet (`editing` is false).
  if (!editing) {
    // ── VIEW MODE — pure text summary, nothing interactive ──────
    // Shows the saved location as plain text + a single Edit button.
    // Consistent across self / child / GeoForcedModal. The user
    // can't accidentally modify anything from this view — they have
    // to explicitly click Edit, which mounts the full editor.
    const gpsPinned = value.latitude != null && value.longitude != null;
    return (
      <div className="location-capture location-capture--summary space-y-2">
        <div className="flex items-start gap-3 rounded-lg border border-emerald-300/60 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2.5">
          <Icon name="location_on" size={18} filled
            className="text-emerald-700 dark:text-emerald-300 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide
                            text-emerald-700/80 dark:text-emerald-300/80">
              {t('geo.saved.label')}
            </div>
            <div className="text-sm text-emerald-900 dark:text-emerald-100 leading-tight break-words">
              {summaryLine ?? t('geo.saved.fallback')}
            </div>
            {summaryPincode && (
              <div className="text-xs text-emerald-800 dark:text-emerald-200 leading-tight mt-0.5">
                <span className="font-semibold">{t('geo.saved.pincode')}:</span>{' '}
                {summaryPincode}
              </div>
            )}
            {gpsPinned && (
              <div className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-0.5 inline-flex items-center gap-1">
                <Icon name="my_location" size={12} />
                {t('geo.saved.gpsPinned')}
              </div>
            )}
          </div>
          {!hideSummaryEdit && (
            <button
              type="button"
              onClick={() => {
                // Reset transient banner / error state so the editor
                // opens clean. The dirty-snapshot logic captures the
                // current value as the baseline on mount of the editor.
                setValidationNote(null);
                setGpsError(null);
                setEditing(true);
              }}
              disabled={disabled}
              className="shrink-0 text-xs font-semibold underline
                         text-emerald-700 dark:text-emerald-300
                         hover:text-emerald-900 dark:hover:text-emerald-100
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('geo.saved.edit')}
            </button>
          )}
        </div>
        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 leading-tight">
          {t('geo.privacy.note')}
        </p>
      </div>
    );
  }

  // ── Editor ───────────────────────────────────────────────────────
  return (
    <div className="location-capture space-y-3">
      {/* Purpose explainer — one short sentence so the user knows why
          we're asking. Translated into 12 languages.
          Hidden when:
            • gpsFirstOnly mode is active (parent shows its own intro), OR
            • hidePurposeBanner prop is true (parent has its own intro
              even in the full alt-method layout — e.g. Register
              step 3 keeps the orange "Where are you playing from?"
              card visible above the component the whole time, so the
              amber banner directly below it was just visual noise on
              a mobile viewport). */}
      {!gpsFirstOnly && !hidePurposeBanner && (
        <p className="text-sm text-amber-900/90 dark:text-amber-200/90 bg-amber-50 dark:bg-amber-900/20 border border-amber-300/60 rounded-lg px-3 py-2">
          <Icon name="info" size={14} className="inline-block align-text-bottom mr-1" />
          {t('geo.purpose')}
        </p>
      )}

      {/* ════════════════════════════════════════════════════════════
          EDIT MODE — Consistent across self / child / GeoForcedModal.
          ════════════════════════════════════════════════════════════
          Once the user clicks Edit, EVERY interactive element stays
          visible. The user sees:
            • the pincode input (with whatever they're typing)
            • the multi-area picker (when applicable)
            • the manual-cascade dropdowns (always available)
            • the GPS button (SELF MODE ONLY — the one mode-specific
              extra; child mode never exposes it because the parent
              must not GPS-lock somebody else's record)
            • the small "Current resolution" badge as live feedback
            • the 15-km validation banner (self mode after GPS)
          Nothing is hidden when a section resolves — the user keeps
          all context until they press Save (or Change to clear).
         ════════════════════════════════════════════════════════════ */}

      {/* Inline current-resolution badge — purely informative. Does
          NOT replace the form. User can keep typing / picking.
          Suppressed in gpsFirstOnly mode: the parent shows its own
          "Location captured" chip to confirm. */}
      {!gpsFirstOnly && resolvedLabel && (
        <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300/60 rounded-lg px-3 py-2">
          <Icon name="check_circle" size={16} filled />
          <span className="flex-1"><span className="font-semibold">{t('geo.saved.label')}: </span>{resolvedLabel}</span>
          <button type="button" onClick={handleReset}
            className="text-xs underline text-emerald-700 dark:text-emerald-300"
            disabled={disabled}>
            {t('geo.change')}
          </button>
        </div>
      )}

      {/* 15-km validation banner — self mode only, after GPS lock. */}
      {!gpsFirstOnly && validationNote && (
        <p className="text-xs text-sky-800 dark:text-sky-200
                      bg-sky-50 dark:bg-sky-900/20
                      border border-sky-300/60 rounded-lg px-3 py-2">
          <Icon name="rule" size={14} className="inline-block align-text-bottom mr-1" />
          {validationNote}
        </p>
      )}

      {/* Pincode input + multi-area picker — hidden in gpsFirstOnly
          mode. Parent re-mounts with gpsFirstOnly=false (via the
          "use a different method" toggle) to surface these inputs. */}
      {!gpsFirstOnly && (
        <div>
          <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 block mb-1">
            {effectiveMode === 'self'
              ? t('geo.self.pinFirst')
              : t('geo.pincode.label')}
          </label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={pincodeInput}
            onChange={(e) => setPincodeInput(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="123456"
            disabled={disabled}
            className={inputClassName ?? DEFAULT_INPUT_CLASS}
          />
          {pincodeError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{pincodeError}</p>
          )}
          {/* Multi-area picker — only when 2+ matches. */}
          {pincodeMatches.length > 1 && (
            <div className="mt-2">
              <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 block mb-1">
                {t('geo.multipleAreas.label')}
              </label>
              <select
                value={pincodeMatches.some((m) => m.area_id === value.area_id) ? String(value.area_id) : ''}
                onChange={(e) => handlePincodeAreaPick(Number(e.target.value))}
                disabled={disabled}
                className={inputClassName ?? DEFAULT_SELECT_CLASS}
              >
                <option value="">— {t('geo.multipleAreas.placeholder')} —</option>
                {pincodeMatches.map((m) => (
                  <option key={m.area_id} value={m.area_id}>
                    {m.area_name} — {m.district_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* GPS step — SELF MODE ONLY (the only mode-specific extra).
          Child mode never shows this: a parent must not GPS-lock
          their child's record (would mis-attribute). Always visible
          in self mode whether or not the pincode has resolved — the
          user can choose to lock GPS first OR pincode first. */}
      {effectiveMode === 'self' && !gpsHidden && (
        <div className={gpsFirstOnly ? '' : 'border-t border-neutral-200 dark:border-neutral-800 pt-3'}>
          {/* "Now lock your GPS" preamble — hidden when the parent
              has its own framing (Register step 3) because the GPS
              CTA is already visually unmistakable just below.
              2026-05-21 — Previously this rendered in any non-
              gpsFirstOnly self-mode usage, including when the alt-
              method panel was expanded in Register; on mobile it
              read as a redundant micro-heading right above the
              already-big orange button. Now suppressed whenever
              hidePurposeBanner is true (the same flag the parent
              uses to declare it owns the framing copy). */}
          {!gpsFirstOnly && !hidePurposeBanner && (
            <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
              <Icon name="my_location" size={12}
                className="inline-block align-text-bottom mr-1" />
              {t('geo.self.thenGps')}
            </p>
          )}
          {/* 2026-05-21 — Recolored from fuchsia to brand orange so
              the GPS CTA matches the rest of the player surface
              (auth-cta, hero buttons, etc.). The fuchsia was a
              hold-over from an early prototype and clashed against
              every screen it appeared on.
              Also: the inline `· geo.gps.benefit` tagline used to
              wrap the button text onto two lines on narrow phones.
              Moved that copy to a small caption below the button, so
              the button itself always shows the label on one line. */}
          {/* 2026-05-21 — Trimmed the GPS CTA size. Previously this
              was `w-full py-3 text-base` which made it visually
              dominate the entire step on a mobile viewport — the
              user's screenshot showed it taking up roughly the same
              vertical space as the "Where are you playing from?"
              intro card right above it.
              Now:
                • mx-auto + max-w-[18rem] so it stops at a sensible
                  CTA width on wide screens but still scales down
                  cleanly on the narrowest phones
                • py-2 (instead of py-3) trims ~8px of vertical chrome
                • text-sm + slightly smaller icon (16) keeps the
                  button readable without dominating the screen
              The button still wins visually as the primary CTA
              because it's the only orange element in the GPS-first
              layout — it just no longer overpowers the rest of the
              step. */}
          <button
            type="button"
            onClick={handleGps}
            disabled={disabled || gpsBusy}
            className="mx-auto block w-full max-w-[18rem] inline-flex items-center justify-center gap-2 px-4 py-2
                       text-white text-sm font-bold tracking-wide rounded-xl
                       bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                       hover:from-brand-300 hover:via-brand-400 hover:to-brand-500
                       border border-white/15 shadow-lg shadow-brand-600/30
                       disabled:opacity-50 disabled:cursor-not-allowed
                       active:scale-[.98] transition"
          >
            <Icon name={gpsBusy ? 'progress_activity' : 'my_location'}
              size={16} className={gpsBusy ? 'animate-spin' : ''} filled />
            <span className="whitespace-nowrap">{t('geo.gps.button')}</span>
          </button>
          <p className="text-[11px] text-center text-neutral-500 dark:text-neutral-400 mt-1.5">
            {t('geo.gps.benefit')}
          </p>
          {gpsError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{gpsError}</p>
          )}
        </div>
      )}

      {/* Manual fallback toggle — always available in both modes.
          Hidden in gpsFirstOnly mode (parent has its own toggle).
          2026-05-21 — Recolored fuchsia → neutral arena/neon so the
          link reads as a calm secondary affordance instead of a
          competing CTA in the player-signup palette. */}
      {!gpsFirstOnly && (
        <button
          type="button"
          onClick={() => setManualOpen((v) => !v)}
          className="text-xs text-neutral-600 dark:text-neutral-300 underline underline-offset-2 hover:text-neutral-800 dark:hover:text-white"
          disabled={disabled}
        >
          {manualOpen ? t('geo.manual.close') : t('geo.manual.title')}
        </button>
      )}
      {!gpsFirstOnly && manualOpen && (
        <div className="space-y-2 border-t border-neutral-200 dark:border-neutral-800 pt-3">
          {/* Manual-cascade pincode picker — appears only when the
              user picked an area via the cascade AND the area maps to
              2+ pincodes (the /api/geo/areas/:id/pincodes call
              returned multiple rows). Renders ABOVE the cascade so
              the user can complete the missing pincode_id without
              re-scrolling. Single-pincode auto-fill skips this UI
              entirely (handled inside handleAreaPick). */}
          {cascadePincodeOptions.length > 1 && (
            <div>
              <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 block mb-1">
                {t('geo.manual.pickPincode', { count: cascadePincodeOptions.length })}
              </label>
              <select
                value={value.pincode_id ?? ''}
                onChange={(e) => handleCascadePincodePick(Number(e.target.value))}
                disabled={disabled}
                className={inputClassName ?? DEFAULT_SELECT_CLASS}
              >
                <option value="">— {t('geo.manual.pickPincodePlaceholder')} —</option>
                {cascadePincodeOptions.map((p) => (
                  <option key={p.pincode_id} value={p.pincode_id}>
                    {p.pincode}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* State dropdown */}
          <div>
            <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 block mb-1">
              {t('geo.state.label')}
            </label>
            <select
              value={value.state_id ?? ''}
              onChange={(e) => handleStatePick(Number(e.target.value))}
              disabled={disabled}
              className={inputClassName ?? DEFAULT_SELECT_CLASS}
            >
              <option value="">— {t('geo.state.placeholder')} —</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          {/* District dropdown — visible once state picked */}
          {value.state_id && (
            <div>
              <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 block mb-1">
                {t('geo.district.label')}
              </label>
              <select
                value={value.district_id ?? ''}
                onChange={(e) => handleDistrictPick(Number(e.target.value))}
                disabled={disabled || districts.length === 0}
                className={inputClassName ?? DEFAULT_SELECT_CLASS}
              >
                <option value="">— {t('geo.district.placeholder')} —</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}
          {/* Area dropdown — visible once district picked */}
          {value.district_id && (
            <div>
              <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 block mb-1">
                {t('geo.area.label')}
              </label>
              <select
                value={value.area_id ?? ''}
                onChange={(e) => handleAreaPick(Number(e.target.value))}
                disabled={disabled || areas.length === 0}
                className={inputClassName ?? DEFAULT_SELECT_CLASS}
              >
                <option value="">— {t('geo.area.placeholder')} —</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Privacy note — short, present in 12 languages. */}
      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 leading-tight">
        {t('geo.privacy.note')}
      </p>

      {/* Required-but-empty visual hint */}
      {required && !hasSomething && !resolvedLabel && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {t('geo.required.hint')}
        </p>
      )}
    </div>
  );
}
